import { Injectable } from "@angular/core";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import { BasiqActions } from "@bitwarden/web-vault/app/services/syncing/basiq/actions/basiq.actions";
import { Origin, SyncStatusEnum } from "@bitwarden/web-vault/app/models/types/general-types";
import { PartialTransactionDeleteError } from "@bitwarden/web-vault/app/services/store/transaction/transaction.store.collection";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { GlossDateFactory } from "@bitwarden/web-vault/app/models/data/shared/gloss-date/gloss-date.factory";
import { GlossQuantityUtils } from "@bitwarden/web-vault/app/models/data/shared/gloss-quantity";
import { BasiqPulledTransaction } from "@bitwarden/web-vault/app/models/types/transaction.types";
import { TransactionView } from "@bitwarden/web-vault/app/models/view/transaction/transaction.view";
import { BasiqAccountResponse } from "@bitwarden/web-vault/app/models/types/basiq.types";
import { SourceTransactionsView } from "@bitwarden/web-vault/app/models/view/source-transactions/source-transactions.view";
import { BasiqIoTransactionType } from "@bitwarden/web-vault/app/models/types/basiq-io-transaction.types";
import { enrichTransaction } from "@bitwarden/web-vault/app/models/view/transaction/transaction.utils";
import {
  isCreditType,
  isMortgageType,
} from "@bitwarden/web-vault/app/shared/utils/helper.account/account-type";
import { AllocationUtils } from "@bitwarden/web-vault/app/models/store/allocations/allocation.utils";

@Injectable({
  providedIn: "root",
})
export class BasiqTransactionAction extends BasiqActions {
  private sortedExistingTransactions: TransactionView[] = [];
  private startFrom: string;
  rawTransactions: BasiqPulledTransaction = { data: [] };
  syncStatus: SyncStatusEnum;

  async pullTransactions(account: AccountView) {
    try {
      const processMessage = `Pulling Transactions of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      this.rawTransactions = await this.rawBasiqTransactions(account, true);
      this.appState.endProcess("importing-data");
      return !!this.rawTransactions;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "Pulling transactions",
        SyncStatusEnum.cantPullTransactions,
      );
      this.rawTransactions = null;
      this.syncStatus = SyncStatusEnum.cantPullTransactions;
      this.glossSyncService.confirmationDialog.notify("error", "error", "somethingWentWrong");
      this.appState.endProcess("importing-data");
      throw error;
    }
  }
  async rawBasiqTransactions(account: AccountView, hasTransactions = false) {
    try {
      this.startFrom = null;
      if ((await this.hasTransactions()) || hasTransactions) {
        this.sortExistingTransactions(account);
        this.startFrom = this.lastTransactionDate(account) || null;
      }

      await this.removePendingTransactions(account);
      return await this.allTransactions(this.startFrom, account);
    } catch (error) {
      this.testLogService.testLog({
        level: "Pulling transactions",
        account: account.name,
        message: "Could not pull transactions",
        reasons: "Network issues, Basiq internal issues",
        error: error,
      });
      throw error;
    }
  }

  async removePending(account: AccountView) {
    try {
      const transactions = this.transactionStore.enrichTransaction.collection();
      const sourceTransactions = this.importStore.sourceTransaction.sourceTransactionViews();
      const pendingTransactions = transactions.filter(
        (transaction: TransactionView) =>
          transaction.status === "pending" && transaction.accountLink.id === account.id,
      );
      if (pendingTransactions.length === 0) {
        return;
      }

      // The source-transaction signal is `null` until its first load and again after `clear()`
      // (logout / vault reset); a loaded-but-empty store is `[]`. A `null` signal is "unknown", not
      // "empty" — we have no view of the source rows. Two unsafe options to avoid: deleting the pending
      // rows now would strand their vault source rows for good (Basiq has no orphan sweep); skipping the
      // removal but letting the pull continue would re-import against stale pending rows, which the
      // duplicate check mishandles (a same-id re-fetch drops the posted replacement so the pending row
      // sticks; a new-id posted row saves beside the pending one as a visible duplicate). So fail fast
      // and abort the whole pull — the same "a null live set is unknown, not empty" principle Fiskil
      // applies to its transaction/enrich live-set (Fiskil treats its own source signal as `?? []`
      // because it orphan-sweeps by account; Basiq has no such sweep, so a null source view here is
      // genuinely unusable). The next sync runs cleanly once the source store is loaded. An
      // empty-but-loaded `[]` is a real "no sources" state and still removes the pending rows below.
      // #4703 review.
      if (sourceTransactions == null) {
        throw new Error(
          "Cannot remove pending transactions: source-transaction store is not loaded (null).",
        );
      }

      // Match ONLY the source rows of the pending rows being removed — never an account-wide sweep,
      // which could delete the still-live source row of a transaction that was moved to another
      // account (#4703 review). Match on EITHER link direction: source.sourceId === pending.id (the
      // back link, always set at creation in basiq.mapper.transaction.ts) OR source.id ===
      // pending.sourceLink.id (the forward link), so a pending row with a blank/stale link on one
      // side still has its own source removed.
      //
      // Deliberately NOT scoped by sourceTransaction.accountId: that id is not rewritten when a
      // transaction is reassigned to another account, so a pending row moved ONTO this account keeps a
      // source row stamped with its old accountId, and an accountId filter would strand it. The
      // pending-id match is already exact (sourceId / id are unique), so the account filter adds no
      // protection — a moved-away transaction simply isn't in the pending set — and only risks misses.
      const pendingIds = new Set(pendingTransactions.map((transaction) => transaction.id));
      const pendingSourceLinkIds = new Set(
        pendingTransactions.map((transaction) => transaction.sourceLink?.id),
      );
      const sourceToDelete = sourceTransactions.filter(
        (sourceTransaction) =>
          pendingIds.has(sourceTransaction.sourceId) ||
          pendingSourceLinkIds.has(sourceTransaction.id),
      );

      // Delete the source rows BEFORE the pending rows. Both are batched writes that commit one cipher
      // at a time (VaultWriter.commitDelete loops per vid), so either can partially commit and then
      // throw, leaving the in-memory models untouched — the caller can't tell which rows actually
      // landed. (The transaction store surfaces a partial as PartialTransactionDeleteError; the source
      // store uses the base deleteFromVault, which just lets the raw vault error propagate.) If the
      // pending rows were deleted first and that write partially failed, the rows that did commit would
      // lose their source rows for good after a reload — Basiq has no orphan sweep (unlike Fiskil).
      // Sources-first lets every failure order recover on the next sync instead: if the source delete
      // throws, the pending delete never runs, so the pending rows survive and are retried; if the
      // pending delete throws after the sources are gone, the pending rows that didn't commit survive
      // and are simply re-matched next sync (their sources already deleted, nothing left to strand).
      // One batched deleteFromVault per store, so deleteWithLinkageRepair scans each collection once
      // instead of once per row. #4703.
      if (sourceToDelete.length > 0) {
        await this.importStore.sourceTransaction.deleteFromVault(sourceToDelete);
      }
      await this.transactionStore.transaction.deleteFromVault(pendingTransactions);

      this.testLogService.testLog({
        level: "Pulling transactions",
        message: "Removed pending transactions",
        count: pendingTransactions.length,
        "source rows removed": sourceToDelete.length,
      });
    } catch (error) {
      // A PartialTransactionDeleteError wraps the real vault failure on `cause` and carries only a
      // generic "a follow-up write did not finish" message — and batching makes the multi-cipher
      // partial the common failure, not the rare one. Log the underlying `cause` and flag the partial
      // state (as checkBalanceValue does for the CBAT delete), so the root cause isn't discarded.
      // Rethrow the original so the pull is still recorded as failed and the next sync self-heals.
      const cause = error instanceof PartialTransactionDeleteError ? error.cause : error;
      this.testLogService.testLog({
        level: "Pulling transactions",
        message:
          error instanceof PartialTransactionDeleteError
            ? "Could not remove pending transactions (partial delete)"
            : "Could not remove pending transactions",
        reasons: "undefined properties, issues with deleting from vault",
        error: cause,
      });

      throw error;
    }
  }

  async removePendingTransactions(account: AccountView) {
    return await this.removePending(account);
  }

  async hasTransactions(): Promise<boolean> {
    const transactions = this.transactionStore.transaction.transactionViews();
    return transactions.length !== 0;
  }

  private sortExistingTransactions(account: AccountView) {
    try {
      const transactions = this.transactionStore.transaction.transactionViews();

      this.sortedExistingTransactions = transactions.sort(
        (a, b) => a.postDate.getTime() - b.postDate.getTime(),
      );
    } catch (error) {
      this.testLogService.testLog({
        level: "Pulling Transactions",
        account: account.name,
        message: "Something went wrong while sorting existing transactions",
        reasons: "undefined transactions, undefined property(transactionDate)",
        error: error,
      });
      throw error;
    }
  }

  lastTransactionDate(basiqAccountInGloss: AccountView) {
    return this.getLastDate(basiqAccountInGloss);
  }

  getLastDate(basiqAccountInGloss: AccountView) {
    try {
      const pendingTransactions = this.sortedExistingTransactions.filter(
        (transaction) =>
          transaction.status === TransactionStatusEnum.pending &&
          transaction.definition !== TransactionStatusEnum.closing &&
          transaction.accountLink.id === basiqAccountInGloss.id,
      );
      const postedTransactions = this.sortedExistingTransactions.filter(
        (transaction) =>
          transaction.accountLink.id === basiqAccountInGloss.id &&
          transaction.status == "posted" &&
          transaction.definition !== TransactionStatusEnum.closing,
      );

      const earliestPendingAccountTransaction = pendingTransactions.length
        ? pendingTransactions[0]
        : null;

      const latestPostedAccountTransaction = postedTransactions.length
        ? postedTransactions[postedTransactions.length - 1]
        : null;
      const earliestPendingDate = earliestPendingAccountTransaction
        ? earliestPendingAccountTransaction.postDate.getTime()
        : 0;

      const lastPostedDate = latestPostedAccountTransaction
        ? latestPostedAccountTransaction.postDate.getTime()
        : 0;

      let latestAccountTransaction;

      if (earliestPendingDate && lastPostedDate) {
        latestAccountTransaction = Math.min(earliestPendingDate, lastPostedDate);
      } else if (earliestPendingDate) {
        latestAccountTransaction = earliestPendingDate;
      } else if (lastPostedDate) {
        latestAccountTransaction = lastPostedDate;
      }

      this.testLogService.testLog({
        level: "Pulling Transactions",
        account: basiqAccountInGloss.name,
        message: "Data on latest transactions(posted & pending)",
        "is there pending transactions": earliestPendingDate ? "yes" : "no",
        "is there posted transactions": lastPostedDate ? "yes" : "no",
        "time stamps": `earliest pending : ${earliestPendingDate}, last posted : ${lastPostedDate}, selected(minimum one): ${latestAccountTransaction}`,
        "earliest pending transaction": earliestPendingAccountTransaction
          ? earliestPendingAccountTransaction.description +
            "@" +
            earliestPendingAccountTransaction.postDate +
            ", created @" +
            earliestPendingAccountTransaction.dateCreated
          : "there is no pending transactions",
        "last posted transaction": latestPostedAccountTransaction
          ? latestPostedAccountTransaction.description +
            "@" +
            latestPostedAccountTransaction.postDate
          : "there is no posted transactions",
      });

      return latestAccountTransaction
        ? this.dateFormat.getDateStringFromStamp(latestAccountTransaction)
        : null;
    } catch (error) {
      this.testLogService.testLog({
        level: "Pulling Transactions",
        message:
          "Something went wrong while trying to determine starting date to pull transactions",
        reasons: "undefined transactions, undefined property(transactionDate)",
        error: error,
      });
      throw error;
    }
  }

  private async allTransactions(lastTransactionsDate: string, account: AccountView) {
    const result: { data: BasiqIoTransactionType[] } = { data: [] };
    let nextLoop = true;
    let nextUrlCall = null;

    while (nextLoop) {
      try {
        const transactionsResponse: any = await this.basiqDataApi.getTransactionsFromBasiq(
          lastTransactionsDate,
          account,
          nextUrlCall,
        );

        if (!transactionsResponse) {
          throw new Error("Transactions not retrieved.");
        }

        nextUrlCall = transactionsResponse?.nextPath;
        result.data = result.data.concat(transactionsResponse.transactions);

        nextLoop = nextUrlCall;
      } catch (error) {
        nextLoop = false; // Exit the loop
        throw error;
      }
    }

    this.testLogService.testLog({
      level: "Retrieving Transactions",
      account: account.name,
      message:
        "pulled transactions info: It should bring back all the transactions that happened on and after the starting date(aka, minimum of the earliest pending and last posting )",
      transactions: result.data,
    });
    return result;
  }

  async saveSourceTransactions(
    validatedTransactions: TransactionView[],
    mappedSourceTransactions: SourceTransactionsView[],
    account: AccountView,
  ) {
    try {
      const processMessage = `Saving Source Transactions of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      const validSourceTransactions = mappedSourceTransactions.filter((st) =>
        validatedTransactions.some((t) => t.sourceLink.id === st.id),
      );
      if (validSourceTransactions.length > 0) {
        const isSaved = await this.importStore.sourceTransaction.saveToVault(
          validSourceTransactions,
          true,
        );
        if (!isSaved) {
          const error = new Error("Could not save source transactions");
          await this.glossSyncService.logError(
            [account],
            error,
            "Saving Source Transactions",
            SyncStatusEnum.cantSaveSourceTransactions,
          );
          this.syncStatus = SyncStatusEnum.cantSaveSourceTransactions;
        }
        this.appState.endProcess("importing-data");
        return isSaved;
      }

      this.appState.endProcess("importing-data");
      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "Saving Source Transactions",
        SyncStatusEnum.cantSaveSourceTransactions,
      );
      this.syncStatus = SyncStatusEnum.cantSaveSourceTransactions;
      this.appState.endProcess("importing-data");
      return false;
    }
  }

  async saveTransactions(transactions: TransactionView[], account: AccountView) {
    try {
      const processMessage = `Saving Transactions of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      if (transactions.length > 0) {
        if (!(await this.checkBalanceValue(account))) {
          this.appState.endProcess("importing-data");
          return false;
        }

        const isSaved = await this.transactionStore.transaction.saveToVault(transactions, true);
        if (!isSaved) {
          const error = new Error("Could not save transactions");
          await this.glossSyncService.logError(
            [account],
            error,
            "Saving Transactions",
            SyncStatusEnum.cantSaveTransactions,
          );
          this.syncStatus = SyncStatusEnum.cantSaveTransactions;
        }
        this.appState.endProcess("importing-data");
        return isSaved;
      }

      this.appState.endProcess("importing-data");
      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "Saving Transactions",
        SyncStatusEnum.cantSaveTransactions,
      );
      this.syncStatus = SyncStatusEnum.cantSaveTransactions;
      this.appState.endProcess("importing-data");
      return false;
    }
  }

  async checkBalanceValue(account: AccountView) {
    try {
      if (!this.startFrom) {
        return true;
      }
      const accountBalanceValuesFromStartDate = this.transactionStore.enrichTransaction
        .collection()
        .filter(
          (t) =>
            t.postDate >= new Date(this.startFrom) &&
            t.definition === TransactionStatusEnum.closing &&
            t.bankImportedBalance &&
            t.accountLink.id === account.id,
        );

      /** Delete existing balance values(CBAT transactions) as we will create new ones based on
       * newly imported transactions */
      if (accountBalanceValuesFromStartDate.length > 0) {
        await this.transactionStore.transaction.deleteFromVault(accountBalanceValuesFromStartDate);
      }
      return true;
    } catch (error) {
      // A PartialTransactionDeleteError means the CBAT delete only half-completed (a survivor's
      // linkage repair committed but a delete write failed, or vice versa). Log its underlying
      // `cause` — the real failure — rather than the wrapper, and flag the partial state, so the
      // root cause and the "a write landed" signal aren't discarded. #4442 / #4504 / #4702.
      const cause = error instanceof PartialTransactionDeleteError ? error.cause : error;
      await this.glossSyncService.logError(
        [account],
        cause,
        error instanceof PartialTransactionDeleteError
          ? "Removing CBAT (partial delete)"
          : "Removing CBAT",
        SyncStatusEnum.cantSaveCBAT,
      );
      this.syncStatus = SyncStatusEnum.cantSaveCBAT;
      return false;
    }
  }

  async saveCBAT(account: AccountView) {
    try {
      const processMessage = `Generating CBAT of ${account.name}`;
      this.appState.startProcess("importing-data", processMessage);
      const allTransactions = this.transactionStore.enrichTransaction.collection();

      const accountTransactions = allTransactions.filter((t) => {
        return t.accountLink.id === account.id;
      });
      const balanceFomTransactions = accountTransactions.reduce(
        (acc, tran) => acc + tran.c_valuation.value.amount,
        0,
      );
      const basiqAccountResponse = await this.basiqDataApi.accounts();
      const basiqBalance = this.getBalanceOnAccount(basiqAccountResponse, account);

      if (basiqBalance.toFixed(2) !== balanceFomTransactions.toFixed(2)) {
        const cBat = this.newBasiqCBAT(basiqBalance, balanceFomTransactions, account);
        await this.transactionStore.transaction.save(cBat);
      }

      this.appState.endProcess("importing-data");
      return true;
    } catch (error) {
      await this.glossSyncService.logError(
        [account],
        error,
        "Creating CBAT",
        SyncStatusEnum.cantSaveCBAT,
      );
      this.syncStatus = SyncStatusEnum.cantSaveCBAT;
      this.appState.endProcess("importing-data");
      return false;
    }
  }

  getBalanceOnAccount(basiqAccountResponse: BasiqAccountResponse, account: AccountView) {
    const basiqAccount = basiqAccountResponse.accounts.find(
      (ba) => ba.id === account.basiqAccountId,
    );

    if (isCreditType(basiqAccount.class.type)) {
      return Number(basiqAccount.availableFunds) - Number(basiqAccount.creditLimit);
    }

    if (isMortgageType(basiqAccount.class.type)) {
      return Number(basiqAccount.balance);
    }

    return Number(basiqAccount.availableFunds);
  }

  private newBasiqCBAT(balanceOnAccount: number, transactionsTotal: number, account: AccountView) {
    const transaction = new TransactionView();

    transaction.glossPostDate = GlossDateFactory.createFromDate(new Date());
    transaction.bankImportedBalance = balanceOnAccount;
    transaction.bankImportedBalanceOrigin = Origin.basiq;
    transaction.quantity = GlossQuantityUtils.fromModel({
      cvrt: 1,
      ccy: "AUD",
      cvsym: "",
      qty: {
        amt: 0,
        sym: "AUD",
        prcs: 8,
      },
    });
    transaction.sourceLink = { id: null };
    transaction.accountLink = { id: account.id };
    transaction.allocations = AllocationUtils.createDefaultAllocations(0);
    transaction.description = `CBAT on basiq sync ( Balance on Account : ${balanceOnAccount}, transactions total(BATS included): ${transactionsTotal}`;
    transaction.valuationPrice = null;
    transaction.linkedTo = [];
    transaction.definition = TransactionStatusEnum.closing;
    transaction.kind = "";

    enrichTransaction(transaction);
    return transaction;
  }
}
